import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { ChevronDown, ChevronRight, TrendingDown, Droplets, Users, AlertTriangle, Settings } from 'lucide-react';
import { Progress } from '../ui/progress';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const hierarchyData = [
  {
    wtp: 'WTP-1',
    capacity: '5 MLD',
    output: 4800,
    status: 'good',
    ohts: [
      {
        name: 'OHT-1',
        volume: 2400,
        nrw: 180,
        nrwPercent: 7.5,
        status: 'good',
        dmas: [
          { name: 'DMA-01', volume: 1000, nrw: 90, nrwPercent: 9.0, households: 200, lpcd: 85, status: 'good' },
          { name: 'DMA-02', volume: 1220, nrw: 90, nrwPercent: 7.4, households: 250, lpcd: 82, status: 'good' },
        ]
      },
      {
        name: 'OHT-2',
        volume: 2400,
        nrw: 288,
        nrwPercent: 12.0,
        status: 'warning',
        dmas: [
          { name: 'DMA-03', volume: 1100, nrw: 132, nrwPercent: 12.0, households: 220, lpcd: 79, status: 'warning' },
          { name: 'DMA-04', volume: 1012, nrw: 156, nrwPercent: 15.4, households: 200, lpcd: 75, status: 'critical' },
        ]
      },
    ]
  },
  {
    wtp: 'WTP-2',
    capacity: '3 MLD',
    output: 2900,
    status: 'good',
    ohts: [
      {
        name: 'OHT-3',
        volume: 2900,
        nrw: 232,
        nrwPercent: 8.0,
        status: 'good',
        dmas: [
          { name: 'DMA-05', volume: 1334, nrw: 116, nrwPercent: 8.7, households: 280, lpcd: 88, status: 'good' },
          { name: 'DMA-06', volume: 1334, nrw: 116, nrwPercent: 8.7, households: 260, lpcd: 86, status: 'good' },
        ]
      },
    ]
  },
];

const FlowRow = ({ data, level = 0, type }) => {
  const [expanded, setExpanded] = useState(level === 0);
  
  const paddingLeft = `${level * 2}rem`;
  
  const statusBadge = (status) => {
    const classes = 
      status === 'good' ? 'bg-success/10 text-success border-success/20' :
      status === 'warning' ? 'bg-warning/10 text-warning border-warning/20' :
      'bg-destructive/10 text-destructive border-destructive/20';
    return <Badge variant="outline" className={classes}>{status}</Badge>;
  };
  
  if (type === 'wtp') {
    return (
      <>
        <tr className="bg-primary/5 font-medium hover:bg-primary/10">
          <td style={{ paddingLeft }}>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </Button>
              <Droplets className="w-4 h-4 text-primary" />
              <span className="font-semibold">{data.wtp}</span>
              <span className="text-xs text-muted-foreground">({data.capacity})</span>
            </div>
          </td>
          <td className="text-center font-semibold">{(data.output ?? 0).toLocaleString()}</td>
          <td className="text-center">-</td>
          <td className="text-center">-</td>
          <td className="text-center">-</td>
          <td className="text-center">-</td>
          <td className="text-center">{statusBadge(data.status)}</td>
        </tr>
        {expanded && data.ohts.map((oht, idx) => (
          <FlowRow key={idx} data={oht} level={level + 1} type="oht" />
        ))}
      </>
    );
  }
  
  if (type === 'oht') {
    return (
      <>
        <tr className="hover:bg-muted/50">
          <td style={{ paddingLeft }}>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </Button>
              <div className="w-2 h-2 rounded-full bg-secondary" />
              <span>{data.name}</span>
            </div>
          </td>
          <td className="text-center">{(data.volume ?? 0).toLocaleString()}</td>
          <td className="text-center">
            <span className={`font-semibold ${
              (data.nrwPercent ?? 0) < 10 ? 'text-success' : 
              (data.nrwPercent ?? 0) < 15 ? 'text-warning' : 'text-destructive'
            }`}>
              {data.nrw ?? 0} ({data.nrwPercent ?? 0}%)
            </span>
          </td>
          <td className="text-center">-</td>
          <td className="text-center">-</td>
          <td className="text-center">-</td>
          <td className="text-center">{statusBadge(data.status)}</td>
        </tr>
        {expanded && data.dmas.map((dma, idx) => (
          <FlowRow key={idx} data={dma} level={level + 1} type="dma" />
        ))}
      </>
    );
  }
  
  if (type === 'dma') {
    return (
      <tr className="hover:bg-muted/50">
        <td style={{ paddingLeft }}>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6" />
            <div className="w-2 h-2 rounded bg-accent" />
            <span>{data.name}</span>
          </div>
        </td>
        <td className="text-center">{(data.volume ?? 0).toLocaleString()}</td>
        <td className="text-center">
          <span className={`font-semibold ${
            (data.nrwPercent ?? 0) < 10 ? 'text-success' : 
            (data.nrwPercent ?? 0) < 15 ? 'text-warning' : 'text-destructive'
          }`}>
            {data.nrw ?? 0} ({data.nrwPercent ?? 0}%)
          </span>
        </td>
        <td className="text-center">{data.households ?? 0}</td>
        <td className="text-center">{data.lpcd}</td>
        <td className="text-center">
          {data.nrwPercent > 12 ? (
            <Badge variant="destructive" className="text-xs">High NRW</Badge>
          ) : (
            <span className="text-muted-foreground text-xs">-</span>
          )}
        </td>
        <td className="text-center">{statusBadge(data.status)}</td>
      </tr>
    );
  }
};

export const FlowHierarchy = ({ selectedVillage, selectedBlock }) => {
  const [flowData, setFlowData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadFlowData();
  }, []);

  const loadFlowData = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/analytics/flow-hierarchy`);
      const data = await response.json();
      setFlowData(data);
    } catch (error) {
      console.error('Error loading flow hierarchy:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading || !flowData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading flow hierarchy...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Title */}
      <div>
        <h1 className="text-3xl font-bold text-foreground mb-2">Flow Hierarchy</h1>
        <p className="text-muted-foreground">Detailed water flow from WTP to households with loss tracking</p>
      </div>

      {/* LPCD Settings Display */}
      <Card className="bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Settings className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
                Calculation Settings (from System Configuration)
              </p>
              <div className="grid grid-cols-3 gap-4 text-xs">
                <div>
                  <span className="text-blue-700 dark:text-blue-300">Target LPCD:</span>
                  <span className="ml-2 font-semibold text-blue-900 dark:text-blue-100">{flowData.settings_used.lpcd} L</span>
                </div>
                <div>
                  <span className="text-blue-700 dark:text-blue-300">Persons/Household:</span>
                  <span className="ml-2 font-semibold text-blue-900 dark:text-blue-100">{flowData.settings_used.persons_per_household}</span>
                </div>
                <div>
                  <span className="text-blue-700 dark:text-blue-300">NRW Target:</span>
                  <span className="ml-2 font-semibold text-blue-900 dark:text-blue-100">{flowData.settings_used.nrw_target}%</span>
                </div>
              </div>
              <p className="text-xs text-blue-700 dark:text-blue-300 mt-2">
                All flow volumes calculated using these settings. Change in Settings → NRW Thresholds to update.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Total Supply (WTPs)</p>
                <p className="text-2xl font-bold">{flowData.source.volume} {flowData.source.unit}</p>
                <p className="text-xs text-muted-foreground">{flowData.source.count} WTPs</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Droplets className="w-6 h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Total Delivered</p>
                <p className="text-2xl font-bold">{flowData.households.volume} {flowData.households.unit}</p>
                <p className="text-xs text-muted-foreground">{flowData.households.lpcd_used} LPCD</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-success/10 flex items-center justify-center">
                <Droplets className="w-6 h-6 text-success" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Total NRW</p>
                <p className="text-2xl font-bold text-destructive">{flowData.total_nrw.volume} {flowData.total_nrw.unit}</p>
                <p className="text-xs text-muted-foreground">Current: {flowData.total_nrw.current}% (Target: {flowData.total_nrw.target}%)</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-destructive/10 flex items-center justify-center">
                <TrendingDown className="w-6 h-6 text-destructive" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Households Served</p>
                <p className="text-2xl font-bold">{(flowData.households?.count ?? 0).toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center">
                <Users className="w-6 h-6 text-accent" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Hierarchy Table */}
      <Card>
        <CardHeader>
          <CardTitle>WTP → OHT → DMA → Household Flow Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th className="w-1/3">Infrastructure</th>
                  <th className="text-center">Volume (m³/day)</th>
                  <th className="text-center">NRW (m³/%)</th>
                  <th className="text-center">Households</th>
                  <th className="text-center">Per HH LPCD</th>
                  <th className="text-center">Alerts</th>
                  <th className="text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {hierarchyData.map((wtp, idx) => (
                  <FlowRow key={idx} data={wtp} level={0} type="wtp" />
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      
      {/* Water Balance Visualization */}
      <Card>
        <CardHeader>
          <CardTitle>Water Balance Flow</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-8">
            {/* WTP to OHT */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">WTP Output → OHT Storage</span>
                <span className="text-sm text-muted-foreground">7,700 m³</span>
              </div>
              <Progress value={100} className="h-3" />
              <div className="flex justify-between mt-1 text-xs text-muted-foreground">
                <span>0%</span>
                <span>100%</span>
              </div>
            </div>
            
            {/* OHT to DMA */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">OHT Distribution → DMAs</span>
                <span className="text-sm text-muted-foreground">7,700 m³</span>
              </div>
              <div className="relative">
                <Progress value={88.3} className="h-3" />
                <div className="absolute top-0 right-0 h-3 bg-destructive/70 rounded-r" style={{ width: '11.7%' }} />
              </div>
              <div className="flex justify-between mt-1 text-xs">
                <span className="text-success">88.3% Delivered (6,800 m³)</span>
                <span className="text-destructive">11.7% Loss (900 m³)</span>
              </div>
            </div>
            
            {/* DMA to Households */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">DMA Supply → Households</span>
                <span className="text-sm text-muted-foreground">6,800 m³</span>
              </div>
              <Progress value={95} className="h-3" indicatorClassName="bg-success" />
              <div className="flex justify-between mt-1 text-xs">
                <span className="text-success">95% Consumption</span>
                <span className="text-muted-foreground">5% System Loss</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Loss Highlights */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Highest NRW Zones</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-destructive/5 rounded-lg border border-destructive/20">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-destructive" />
                  <div>
                    <p className="font-medium">DMA-04</p>
                    <p className="text-xs text-muted-foreground">OHT-2 → DMA-04</p>
                  </div>
                </div>
                <Badge variant="destructive">15.4%</Badge>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-warning/5 rounded-lg border border-warning/20">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-warning" />
                  <div>
                    <p className="font-medium">DMA-03</p>
                    <p className="text-xs text-muted-foreground">OHT-2 → DMA-03</p>
                  </div>
                </div>
                <Badge variant="outline" className="bg-warning/10 text-warning border-warning/20">12.0%</Badge>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Recommended Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex gap-3 p-3 bg-muted rounded-lg">
                <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-primary-foreground">1</span>
                </div>
                <div>
                  <p className="text-sm font-medium">Inspect OHT-2 distribution lines</p>
                  <p className="text-xs text-muted-foreground">High NRW detected in downstream DMAs</p>
                </div>
              </div>
              
              <div className="flex gap-3 p-3 bg-muted rounded-lg">
                <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-primary-foreground">2</span>
                </div>
                <div>
                  <p className="text-sm font-medium">Schedule leak detection in DMA-04</p>
                  <p className="text-xs text-muted-foreground">NRW exceeds 15% threshold</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FlowHierarchy;